// Bundled TopoJSON of all world countries at 1:110m resolution, converted
// once to a GeoJSON FeatureCollection so the map components and our
// "zoom-to-country" logic can both share the same shapes.
//
// 1:110m is plenty of detail for a workforce-distribution map (~94 KB
// gzipped) and avoids the latency / failure modes of a runtime fetch.
//
// Country matching: world-atlas features carry `properties.name` (English
// long-form, e.g. "United States of America", "Russia") and `id` (numeric
// ISO 3166-1 code as a string, e.g. "840"). We expose helpers that
// compare names tolerantly so business strings like "USA" or "Czech
// Republic" still resolve.

import { feature as topoFeature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { Topology } from 'topojson-specification';
import { geoBounds, geoCentroid } from 'd3-geo';

// world-atlas ships TopoJSON as JSON. The package's typings don't include
// the JSON path, so we cast through `unknown` to keep `strict` happy.
// eslint-disable-next-line @typescript-eslint/no-var-requires
import countries110m from 'world-atlas/countries-110m.json';

export interface CountryProperties {
  /** Long-form English name (e.g. "United States of America"). */
  name: string;
}

export type CountryFeature = Feature<Geometry, CountryProperties>;

const COUNTRIES_TOPO = countries110m as unknown as Topology;

export const COUNTRIES: FeatureCollection<Geometry, CountryProperties> =
  topoFeature(COUNTRIES_TOPO, COUNTRIES_TOPO.objects.countries as never) as unknown as
    FeatureCollection<Geometry, CountryProperties>;

// --------------------------------------------------------------- name lookup

/** Aliases used by HR data → world-atlas feature name. Only the lefthand
 * side needs to be lowercased; comparison normalises both sides. Add to
 * this map as new BU country strings appear in the warehouse. */
const NAME_ALIASES: Readonly<Record<string, string>> = {
  usa: 'United States of America',
  'united states': 'United States of America',
  us: 'United States of America',
  uk: 'United Kingdom',
  'great britain': 'United Kingdom',
  britain: 'United Kingdom',
  'czech republic': 'Czechia',
  'south korea': 'South Korea',
  'republic of korea': 'South Korea',
  'north korea': 'North Korea',
  'ivory coast': "Côte d'Ivoire",
  burma: 'Myanmar',
  'east timor': 'Timor-Leste',
  vatican: 'Vatican City',
  uae: 'United Arab Emirates',
  'saudi': 'Saudi Arabia',
  'russia': 'Russia',
  'russian federation': 'Russia',
};

function normalise(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Best-effort country lookup by display name. Returns the matching
 * GeoJSON feature or null when nothing fits. */
export function findCountryFeature(name: string | null | undefined): CountryFeature | null {
  if (!name) return null;
  const norm = normalise(name);
  const aliased = NAME_ALIASES[norm] ?? name;
  const target = normalise(aliased);

  // Exact (normalised) match first, then a contains match for things like
  // "United States" → "United States of America".
  const exact = COUNTRIES.features.find(
    (f) => normalise((f.properties?.name ?? '') as string) === target,
  );
  if (exact) return exact as CountryFeature;

  return (
    COUNTRIES.features.find((f) => {
      const fname = normalise((f.properties?.name ?? '') as string);
      return fname.includes(target) || target.includes(fname);
    }) as CountryFeature | undefined
  ) ?? null;
}

/** Geographic bounds [[w, s], [e, n]] for a country, or null. */
export function boundsForCountry(name: string | null | undefined): [
  [number, number],
  [number, number],
] | null {
  const f = findCountryFeature(name);
  if (!f) return null;
  return geoBounds(f) as [[number, number], [number, number]];
}

/** Centroid [lon, lat] for a country, or null. */
export function centroidForCountryFeature(name: string | null | undefined): [number, number] | null {
  const f = findCountryFeature(name);
  if (!f) return null;
  return geoCentroid(f) as [number, number];
}
